import { computed, inject, Injectable, linkedSignal, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Option } from '@dxc-technology/halstack-angular';
import { TranslateService } from '@ngx-translate/core';
import { CONTRACT_TYPE, DOCUMENT_STATUS, METHOD, PROPOSAL_VERSION_STATUS, YESNO } from '@shared/components/base-classes/base-group/constants';
import { AbstractHttpObservable } from '@shared/types/abstract';
import { HttpMethod } from '@shared/types/hal-client';
import { Dropdowns, MessageError, YesNo } from '@shared/types/type';
import { customSignal } from '@shared/utils/signal.util';
import _ from 'lodash';
import moment from 'moment';
import { BehaviorSubject, forkJoin, from, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, pluck, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CommonService } from './common.service';
import { GroupedMedical, MedicalEnum, MemberMecdialesponse, Question, QuestionItem, ReverseMedicalEnum } from './coverage-information.service';

export const POLICY_TYPES = 'policyTypes';
export const RIDERS = 'riders';
export const COVERAGES = 'coverages';
export const PREMIUM_MODE = 'modeOfPremium';

const POLICY_TYPE_CODES: readonly string[] = Object.values(CONTRACT_TYPE);
const LOOKUP_LIST = [
  'In-patientOverseeprovider',
  'clientType',
  'paymentMethods',
  'premiumCurrency',
  'classOfInsuredPeoples',
  'BankCode',
  'MainInsuredType',
  'InsuredClassforMaternity',
  'LineofBusiness',
] as const;
const SORTED_LOOKUP_LIST = ['ProvinceList'] as const;
const COMMON_LOOKUP_LIST = ['Gender', 'MaritalStatus', 'countryCodes'] as const;
type DropDownKeys =
  | typeof POLICY_TYPES
  | typeof RIDERS
  | typeof COVERAGES
  | typeof PREMIUM_MODE
  | 'providerNetworks'
  | (typeof LOOKUP_LIST)[number]
  | (typeof SORTED_LOOKUP_LIST)[number]
  | (typeof COMMON_LOOKUP_LIST)[number];
export type SPAKDropDowns = Partial<Dropdowns<DropDownKeys>>;
export type CountryDropdown = {
  ProvinceList?: Option[];
  city1?: Option[];
  district1?: Option[];
  subDistrict1?: Option[];
  postalCode1?: Option[];
  city0?: Option[];
  district0?: Option[];
  subDistrict0?: Option[];
  postalCode0?: Option[];
};
export type ApplicationForm = Partial<{
  uuid: string;
  membershipTerm: string;
  benefitSelection: string;
  benefitSelectionMention: string;
  claimPaymentMethod: string;
  bankAccount: string;
  programStatus: string;
  additionalProgramName: string;
  additionalProgramPeriodFrom: string;
  additionalProgramPeriodTo: string;
  totalEmpoyeeBusiness: string;
  reasonNotAllEmployee: string;
  mainInsurerEntitled: string;
  mainInsurerEntitledOption: any;
  depandantsEntitled: string;
  entitledChildBenefit: string;
  policyHolderQuestionarySetID: string;
  coverageQuestionarySetID: string;
  coverageQuestionSet: string;
  bankAccountNumber: string;
  bankAccountOwnerName: string;
  bankBranch: string;
  bankCity: string;
  bankCurrency: string;
  bankName: string;
  esignatureConfirm: YesNo;
  maxAgeChild: string;
  maxAgeMaternity: string;
  maxNumberChild: string;
  maxNumberMaternity: string;
  agreePrivacyPreferences: string;
  agreeProductOffers: string;
  version: string;
  sendToEmail: string;
  isSendToEmail: string;
  isAuthPartyBenefOwner: 'Y' | 'N';
  isEdit: 'Y' | 'N';
  uwApprovalStatus: string;
  uwRemarks: string;
  saleStatus: string;
  salesRemarks: string;
  agreement: YesNo;
}>;

export enum SPAK_SECTION_KEY {
  SECTION1 = 'policyHolderCandidates',
  SECTION2 = 'authorizedParty',
  SECTION3 = 'beneficialOwner',
  SECTION5 = 'coverageInformation',
  ESIGNATURE = 'eSignature',
  SECTION6 = 'declaration',
  SPAK = 'spak',
}

export type SignatureDetail = {
  referenceId: string;
  fileName: string | null;
  fileSize: number | null;
  content: string | null;
  fullName: string;
  jobTitle: string;
};

export type Signature = {
  id: string;
  uploadDate: string | null;
  uploadAt: string | null;
  docs: SignatureDetail[];
};

export type AddressKey = 'postalCode' | 'subDistrict' | 'district' | 'city';
export type AddressFilterKey = 'subDistrict' | 'district' | 'city';

export type OrganizationAddress = {
  organizationBuildingName: string;
  organizationAddressStreet: string;
  organizationAddressState: string;
  organizationAddressTown: string;
  organizationAddressCity: string;
  organizationVillage: string;
  organizationAddressPostalCode: string;
};

@Injectable({
  providedIn: 'root',
})
export class SpakFormService implements AbstractHttpObservable {
  private readonly commonService = inject(CommonService);
  private readonly translateService = inject(TranslateService);
  private readonly picSubject = new BehaviorSubject(null);
  private readonly proposalSubject = new BehaviorSubject(null);
  private readonly dropdownSubject = new BehaviorSubject<SPAKDropDowns>({});
  private readonly signatureSectionSubject = new Subject<void>();
  signatureSectionObservable = this.signatureSectionSubject.asObservable();
  readonly ekycSelectedOption = computed<YesNo>(() => this.#applicationFormData().esignatureConfirm);
  readonly #applicationFormData = signal<ApplicationForm>({});
  private isAgreedTerm: boolean = false;
  agreePrivacyPreferences: WritableSignal<string> = linkedSignal(() => this.#applicationFormData().agreePrivacyPreferences);
  agreeProductOffers: WritableSignal<string> = linkedSignal(() => this.#applicationFormData().agreeProductOffers);
  private readonly statusReportErrorsDefaultValue = new Map([
    [SPAK_SECTION_KEY.SECTION1, []],
    [SPAK_SECTION_KEY.SECTION2, []],
    [SPAK_SECTION_KEY.SECTION3, []],
    [SPAK_SECTION_KEY.SECTION5, []],
    [SPAK_SECTION_KEY.ESIGNATURE, []],
    [SPAK_SECTION_KEY.SECTION6, []],
    [SPAK_SECTION_KEY.SPAK, []],
  ]);
  statusReportErrors = signal<Map<string, MessageError[]>>(new Map(this.statusReportErrorsDefaultValue), {
    equal: _.isEqual,
  });
  totalTransactionsPerYear: string | boolean;
  proposalSignal = toSignal(this.proposal$);
  baseProposalUrl = computed(() => `${environment.API_PROPOSAL_SERVER_URL}/${this.proposalSignal()?.id}`);
  signature = customSignal<Signature>({
    id: '',
    uploadDate: null,
    uploadAt: null,
    docs: [],
  });
  isDisabled = computed(() => {
    const versionStatus = this.proposalSignal()?.versionStatus;

    return (
      (this.applicationFormData().uwApprovalStatus === 'PASS' ||
        (this.applicationFormData().esignatureConfirm === 'N' &&
          this.applicationFormData().isEdit === 'N' &&
          this.applicationFormData().isSendToEmail === 'Y')) &&
      ![PROPOSAL_VERSION_STATUS.RETURNED].includes(versionStatus)
    );
  });
  isEditField = signal(true);
  disableVersion = computed(() => {
    if (!this.isEditField()) {
      return true;
    }
    const versionStatus = this.proposalSignal()?.versionStatus;
    return [
      PROPOSAL_VERSION_STATUS.PENDING_UW_CHECKED,
      PROPOSAL_VERSION_STATUS.PENDING_FOR_UW_CHECKED,
      PROPOSAL_VERSION_STATUS.LOCKED,
      PROPOSAL_VERSION_STATUS.EXPIRED,
      PROPOSAL_VERSION_STATUS.CONFIRMED,
      PROPOSAL_VERSION_STATUS.DECLINE,
      PROPOSAL_VERSION_STATUS.DECLINED,
    ].includes(versionStatus);
  });

  disableReturnedStatus = computed(() => {
    const versionStatus = this.proposalSignal()?.versionStatus;
    return [PROPOSAL_VERSION_STATUS.RETURNED].includes(versionStatus);
  });
  contractTypeCoverDependents = computed(() => ![CONTRACT_TYPE.GCL, CONTRACT_TYPE.GCP].includes(this.proposalSignal()?.contractType));
  questionTableKeys = signal([
    'cannotWorkEmployeeList',
    'canWorkEmployeeList',
    'diagnosedEmployeeList',
    'dependentsCannotWork',
    'dependentsAreDiagnosed',
  ]).asReadonly();
  isDirty = signal({
    [SPAK_SECTION_KEY.SECTION1]: false,
    [SPAK_SECTION_KEY.SECTION2]: false,
    [SPAK_SECTION_KEY.SECTION3]: false,
    [SPAK_SECTION_KEY.SECTION5]: false,
    [SPAK_SECTION_KEY.SECTION6]: false,
  });
  private readonly _defaultSpakLanguage = signal('');
  get defaultSpakLanguage() {
    return this._defaultSpakLanguage();
  }

  set defaultSpakLanguage(language: string) {
    this._defaultSpakLanguage.set(language);
  }

  currentSpakLanguage = computed(() => {
    if (this._defaultSpakLanguage()) return this._defaultSpakLanguage();
    return this.commonService.currentLang();
  });

  get isAgreeSignatureTerm() {
    return this.isAgreedTerm;
  }

  set isAgreeSignatureTerm(value: boolean) {
    this.isAgreedTerm = value;
  }

  get pic$() {
    return this.picSubject.asObservable();
  }

  set pic(pic: any) {
    this.picSubject.next(pic);
  }

  get proposal$() {
    return this.proposalSubject.asObservable();
  }

  set proposal(proposal: any) {
    this.proposalSubject.next(proposal);
  }

  get dropdowns$() {
    return this.dropdownSubject.asObservable();
  }

  set dropdowns(dropdowns: SPAKDropDowns) {
    this.dropdownSubject.next(dropdowns);
  }

  get applicationFormData(): Signal<ApplicationForm> {
    return this.#applicationFormData.asReadonly();
  }

  setApplicationFormData(value: ApplicationForm) {
    this.#applicationFormData.set(value);
  }

  commonHttpObservable<T = any>(
    url: string,
    options: { headers?: Record<string, string>; method: HttpMethod; body?: Record<string, any> } = { method: 'get' }
  ): Observable<T> {
    if (!_.get(options, 'headers.language')) {
      _.set(options, 'headers.language', this.currentSpakLanguage());
    }
    return this.commonService.commonHttpObservable(url, options);
  }

  getDropdowns(lang: string, onFinalized: () => void): void {
    this.getDropdowns$(lang)
      .pipe(
        map((dataArr) => {
          const serializedData = _.reduce(dataArr, (acc, curr) => ({ ...acc, ...curr }), {});
          return serializedData;
        }),
        take(1),
        finalize(onFinalized)
      )
      .subscribe((values) => {
        this.dropdowns = values;
      });
  }

  getDropdowns$(lang: string) {
    return forkJoin([
      this.getPolicyTypeDropdown(lang),
      this.getRidersDropdown(lang),
      this.getCoveragesDropdown(lang),
      this.getModeOfPremiumOptions(lang),
      this.getLookupListDropdown(lang),
      this.getSortedLookupListDropdown(lang),
      this.getCommonLookupListDropdown(lang),
      this.getProviderNetworks$(lang),
    ]);
  }

  getAddress$(body, key: AddressKey): Observable<Option[]> {
    const url = new URL(`${environment.API_GROUP_INTEGRATION_URL}/filterAddress`);
    return this.commonHttpObservable(url.href, { method: 'post', body }).pipe(
      pluck('body'),
      catchError((err) => {
        console.error(err);
        throw err;
      })
    );
  }

  getProvince$(): Observable<Option[]> {
    const url = new URL(`${environment.API_GROUP_INTEGRATION_URL}/filterAddress`);
    url.searchParams.set('filterType', 'province');
    return this.commonHttpObservable(url.href, { method: 'post', body: {} }).pipe(
      pluck('body'),
      catchError((err) => {
        console.error(err);
        throw err;
      })
    );
  }

  getPolicyTypeDropdown(lang: string): Observable<SPAKDropDowns> {
    return this.commonHttpObservable(
      `${environment.API_GROUPCONFIGURATION_SERVER_URL}/product-descriptions?productCode=${encodeURIComponent(POLICY_TYPE_CODES.join(','))}`,
      {
        headers: {
          language: lang,
        },
        method: 'get',
      }
    ).pipe(
      pluck('body'),
      map((items) => {
        const dropdown = items?.map(({ productDescription, productCode }) => ({ label: productDescription, value: productCode }));
        return { [POLICY_TYPES]: dropdown };
      }),
      catchError(() => of({ [POLICY_TYPES]: [] }))
    );
  }

  getRidersDropdown(lang: string): Observable<SPAKDropDowns> {
    return this.commonHttpObservable(`${this.baseProposalUrl()}/availableRiders`, {
      headers: {
        language: lang,
      },
      method: 'get',
    }).pipe(
      pluck('body'),
      map((items) => {
        const dropdown = items?.map((item: any) => _.pick(item, ['label', 'value']));
        return { [RIDERS]: dropdown };
      }),
      catchError(() => of({ [RIDERS]: [] }))
    );
  }

  getCoveragesDropdown(lang: string): Observable<SPAKDropDowns> {
    return this.commonHttpObservable(`${this.baseProposalUrl()}/availableCoverages`, {
      headers: {
        language: lang,
      },
      method: 'get',
    }).pipe(
      pluck('body'),
      map((items) => {
        const dropdown = items?.map((item: any) => _.pick(item, ['label', 'value']));
        return { [COVERAGES]: dropdown };
      }),
      catchError(() => of({ [COVERAGES]: [] }))
    );
  }

  getModeOfPremiumOptions(lang: string): Observable<SPAKDropDowns> {
    const startDate = this.proposalSignal().policyPeriodStart;
    const endDate = this.proposalSignal().policyPeriodEnd;
    const isAbleCustomize = this.proposalSignal().isAbleCustomize;
    const url = `${environment.API_GROUPCONFIGURATION_SERVER_URL}/lookup/modeOfPremium?isAbleToCustominze=${isAbleCustomize}&startDate=${startDate}&endDate=${endDate}`;
    return this.commonHttpObservable(url, {
      headers: {
        language: lang,
      },
      method: 'get',
    }).pipe(
      pluck('body'),
      catchError(() => of({ [PREMIUM_MODE]: [] }))
    );
  }

  //group_master_data
  getLookupListDropdown(lang: string): Observable<SPAKDropDowns> {
    return this.commonHttpObservable(`${environment.API_GROUPCONFIGURATION_SERVER_URL}/lookup?list=${encodeURIComponent(LOOKUP_LIST.join(','))}`, {
      headers: {
        language: lang,
      },
      method: 'get',
    }).pipe(pluck('body'));
  }

  //group_master_data sort by api
  getSortedLookupListDropdown(lang: string): Observable<SPAKDropDowns> {
    return this.commonHttpObservable(
      `${environment.API_GROUPCONFIGURATION_SERVER_URL}/lookup?list=${encodeURIComponent(SORTED_LOOKUP_LIST.join(','))}&sortName=label&sortType=ASC`,
      {
        headers: {
          language: lang,
        },
        method: 'get',
      }
    ).pipe(pluck('body'));
  }

  //common_master_data
  getCommonLookupListDropdown(lang: string): Observable<SPAKDropDowns> {
    return this.commonHttpObservable(`${environment.API_GROUPCONFIGURATION_SERVER_URL}/lookup?list=${encodeURIComponent(COMMON_LOOKUP_LIST.join(','))}`, {
      headers: {
        language: lang,
      },
      method: 'get',
    }).pipe(pluck('body'));
  }

  getProviderNetworks$(lang: string): Observable<SPAKDropDowns> {
    return this.commonHttpObservable(`${environment.API_GROUPCONFIGURATION_SERVER_URL}/lookup/providerNetworks?policyType=GWM`, {
      headers: {
        language: lang,
      },
      method: 'get',
    }).pipe(pluck('body'));
  }

  isBanca(sourceOfBusiness: string) {
    return sourceOfBusiness === 'BankPartnership';
  }

  getStatusReport$(): Observable<boolean> {
    this.commonService.setLoading(true);
    const applicationStatusReportUrl = `${this.baseProposalUrl()}/eKYC/applicationForm/status_report`;
    const statusReportObservables = {
      questionaire: this.getQuestionaireStatusReport(),
      applicationForm: this.commonHttpObservable(applicationStatusReportUrl).pipe(pluck('body')),
    };
    return forkJoin(statusReportObservables).pipe(
      tap(({ questionaire, applicationForm }: any) => {
        const messages: MessageError[] = [...questionaire.message, ...applicationForm.message];
        this.setStatusReportErrors(messages);
      }),
      map(
        ({ questionaire, applicationForm }: any) =>
          ![questionaire.document_status.toLowerCase(), applicationForm.document_status.toLowerCase()].includes('invalid')
      ),
      catchError(() => of(false)),
      finalize(() => this.commonService.setLoading(false))
    );
  }

  setStatusReportErrors(message: MessageError[]) {
    const errors = message.reduce(
      (prev, cur) => {
        let section = cur.resource.split('-')[0] as SPAK_SECTION_KEY;
        if (section && prev.has(section)) {
          const sectionErrors = prev.get(section);
          sectionErrors.push(cur);
          prev.set(section, sectionErrors);
        }

        return prev;
      },
      new Map<SPAK_SECTION_KEY, MessageError[]>(_.cloneDeep(this.statusReportErrorsDefaultValue))
    );

    this.statusReportErrors.set(errors);
  }

  setStatusReportErrorsForSection(sectionKey: SPAK_SECTION_KEY, errorMessages: MessageError[]) {
    this.statusReportErrors.update((prev) => {
      return new Map([...prev, [sectionKey, errorMessages]]);
    });
  }

  setStatusReportErrorsDefaultValue() {
    this.statusReportErrors.set(new Map<string, MessageError[]>(_.cloneDeep(this.statusReportErrorsDefaultValue)));
  }

  getApplicationForm$(): Observable<ApplicationForm> {
    const url = `${this.baseProposalUrl()}/eKYC/applicationForm`;
    return this.commonHttpObservable(url).pipe(map((data) => data.body));
  }

  patchApplicationForm$(key: keyof ApplicationForm, value: string, uuid?: string) {
    const body = { [key]: value };
    const id = this.#applicationFormData().uuid || uuid;
    const url = `${this.baseProposalUrl()}/eKYC/applicationForm/${id}`;
    return this.commonHttpObservable(url, { method: 'patch', body }).pipe(map((data) => data.body));
  }

  patchApplicationFormData$(body: Partial<ApplicationForm>, uuid?: string) {
    const id = this.#applicationFormData().uuid || uuid;
    const url = `${this.baseProposalUrl()}/eKYC/applicationForm/${id}`;
    return this.commonHttpObservable(url, { method: 'patch', body }).pipe(
      map((data) => data.body),
      catchError((err) => {
        console.error(err);
        return of({});
      })
    );
  }

  postApplicationFormById$(uuid?: string) {
    const body = {};
    const id = this.#applicationFormData().uuid || uuid;
    const url = `${this.baseProposalUrl()}/eKYC/applicationForm/${id}`;
    return this.commonHttpObservable(url, { method: 'post', body }).pipe(map((data) => data.body));
  }

  getApplicationFormById$(uuid?: string) {
    const id = this.#applicationFormData().uuid || uuid;
    const url = `${this.baseProposalUrl()}/eKYC/applicationForm/${id}`;
    return this.commonHttpObservable(url).pipe(map((data) => data.body));
  }

  getSignature$(applicationId: string) {
    const url = `${this.baseProposalUrl()}/eKYC/applicationForm/${applicationId}/signature`;
    return this.commonHttpObservable(url).pipe(
      map((data) => data.body),
      map((data: Signature) => {
        if (!data.uploadDate) {
          data.uploadDate = moment(new Date()).format('DD-MMM-YYYY');
        }
        data.uploadAt ??= '';
        data.docs = data.docs.map((doc) => {
          doc.content ??= '';
          doc.fileName ??= '';
          return doc;
        });
        return data;
      })
    );
  }

  saveSignature$(applicationId: string, body: any) {
    const url = `${this.baseProposalUrl()}/eKYC/applicationForm/${applicationId}/signature`;
    return this.commonHttpObservable(url, { method: 'patch', body }).pipe(map((data) => data.body));
  }

  deleteSignatures$(applicationId: string) {
    const url = `${this.baseProposalUrl()}/eKYC/applicationForm/${applicationId}/signature`;
    return this.commonHttpObservable(url, { method: 'del' }).pipe(
      catchError((err) => {
        console.error(err);
        return of({});
      })
    );
  }

  getRegisteredAddresses$() {
    return this.commonHttpObservable(`${this.baseProposalUrl()}/eKYC/policyHolderCandidates`).pipe(
      pluck('body'),
      switchMap(({ originalProspectId }) => {
        if (!originalProspectId) return of({});
        return this.commonHttpObservable(`${environment.API_ORGANIZATION_SERVER_URL}/${originalProspectId}/addresses`, {
          method: 'get',
          headers: { language: 'en' },
        })
          .pipe(
            pluck('body'),
            map((data) =>
              _.pick(data._links?.item[0]?.summary, [
                'organizationBuildingName',
                'organizationAddressStreet',
                'organizationAddressState',
                'organizationAddressTown',
                'organizationAddressCity',
                'organizationVillage',
                'organizationAddressPostalCode',
              ])
            )
          )
          .pipe(
            catchError((err) => {
              console.error(err);
              throw err;
            })
          );
      })
    );
  }

  deleteSignature$(applicationId: string, id: string) {
    const url = `${this.baseProposalUrl()}/eKYC/applicationForm/${applicationId}/signature/${id}`;
    return this.commonHttpObservable(url, { method: 'del' }).pipe(
      catchError((err) => {
        console.error(err);
        return of({});
      })
    );
  }

  syncSignature() {
    this.signatureSectionSubject.next();
  }

  getQuestionaireStatusReport(): Observable<any> {
    const defaultResult = this.getDefaultQuestionaireStatusReport();
    const { coverageQuestionarySetID } = this.applicationFormData();
    const url = `${environment.API_QSN}/questionnaire-sets/${coverageQuestionarySetID}/questionnaires`;
    if (!coverageQuestionarySetID) return of(defaultResult);

    return this.getQuestionnaires$(url).pipe(
      switchMap((url) => this.getQuestionnairesbyId$(url)),
      switchMap((url) => forkJoin({ medicalTable: this.getMedicalTable$(), questions: this.getQuestion$(url) })),
      map(({ questions, medicalTable }) => {
        const messages = [];
        _.map(questions, (question: any, i: number) => {
          if ((!this.validateAnswer(question) && i <= 2) || (!this.validateAnswer(question) && i > 2 && this.contractTypeCoverDependents())) {
            messages.push({
              context: this.questionTableKeys()[i],
              resource: SPAK_SECTION_KEY.SECTION5,
              severity: 'error',
              message: this.translateService.instant('label.common.cannotBeBlank'),
            });
          }
          if (question.answer_value === YESNO.YES && _.isEmpty(medicalTable[this.questionTableKeys()[i]].rows)) {
            messages.push({
              context: ReverseMedicalEnum[this.questionTableKeys()[i]],
              resource: SPAK_SECTION_KEY.SECTION5,
              severity: 'error',
              message: this.translateService.instant('label.common.cannotBeBlank'),
            });
          }
        });
        const documentStatus = _.isEmpty(messages) ? DOCUMENT_STATUS.VALID : DOCUMENT_STATUS.INVALID;
        return {
          document_status: documentStatus,
          message: messages,
        };
      }),
      catchError((err) => {
        console.error(err);
        return of(defaultResult);
      })
    );
  }

  validateAnswer(question: Question) {
    return question.Mandatory === 'N' || (question.Mandatory === 'Y' && question.answer_value);
  }
  getMedicalTable$(): Observable<GroupedMedical> {
    const applicationId = this.applicationFormData().uuid;
    const url = `${this.baseProposalUrl()}/eKYC/applicationForm/${applicationId}/medicalTable`;

    const groupedMedical: GroupedMedical = {
      cannotWorkEmployeeList: {
        rows: [],
      },
      canWorkEmployeeList: {
        rows: [],
      },
      diagnosedEmployeeList: {
        rows: [],
      },
      dependentsCannotWork: {
        rows: [],
      },
      dependentsAreDiagnosed: {
        rows: [],
      },
    };

    return this.commonHttpObservable(url).pipe(
      map((res) => res.body),
      map((tables: any[]) => {
        return tables.reduce((prev, cur: MemberMecdialesponse) => {
          const key = MedicalEnum[cur.medicalType];
          prev[key].rows.push({ ..._.pick(cur, ['memberId', 'medicalType', 'absenceDate', 'status', 'typeDisease', 'diagnosisDate', 'fullName']) });
          return prev;
        }, _.cloneDeep(groupedMedical));
      }),
      catchError((err) => {
        console.error(err);
        throw err;
      })
    );
  }

  getDefaultQuestionaireStatusReport() {
    const statusReport = {
      document_status: DOCUMENT_STATUS.INVALID,
      message: [],
    };
    const message = Array(5)
      .fill(0)
      .map((_, i) => {
        return {
          context: `question_${i + 1}`,
          resource: SPAK_SECTION_KEY.SECTION5,
          severity: 'error',
          message: this.translateService.instant('label.common.cannotBeBlank'),
        };
      });
    statusReport.message = message;
    return statusReport;
  }

  getQuestionnaires$(url: string) {
    return from(this.commonService.callApiForQsn({ url, method: METHOD.GET })).pipe(map((data) => data._links.item.href));
  }

  getQuestionnairesbyId$(url: string) {
    return from(this.commonService.callApiForQsn({ url, method: METHOD.GET })).pipe(map((data) => data._links['questionnaire:questions'].href));
  }

  getQuestion$(url: string) {
    return from(this.commonService.callApiForQsn({ url, method: METHOD.GET })).pipe(
      map((data) => data._links.item),
      map((items: QuestionItem[]) => _.map(items, 'summary'))
    );
  }

  updateIsDirty(key: SPAK_SECTION_KEY, value: boolean = true) {
    this.isDirty.update((old) => {
      const cloned = _.cloneDeep(old);
      return {
        ...cloned,
        [key]: value,
      };
    });
  }

  getDefaultSpakLanguage(): Observable<string> {
    return this.commonHttpObservable(`${environment.API_GROUPCONFIGURATION_SERVER_URL}/defaultLanguage`).pipe(
      map((res) => _.toLower(_.get(res, 'body.defaultLanguage', ''))),
      catchError((error) => {
        console.error(error);
        return of('');
      })
    );
  }

  updateEditFieldFromStorage(): void {
    const groupSalesAct = localStorage.getItem('groupSalesAct');
    if (groupSalesAct) {
      try {
        const parsed = JSON.parse(groupSalesAct);
        const isEdit = parsed?.proposal?.actions?.Edit ?? true;
        this.isEditField.set(isEdit);
      } catch (err) {
        console.error('Failed to parse groupSalesAct:', err);
        this.isEditField.set(true);
      }
    }
  }
}
